import React, { ChangeEvent, useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IOnUpdateOrderEntryItemDTO from "../../../models/DTO/IOnUpdateOrderEntryItemDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import INotification from "../../../models/INotification";
import IOrder from "../../../models/IOrder";
import IOrder_Item from "../../../models/IOrder_Item";
import IOrder_ItemUI from "../../../models/IOrder_ItemUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import "./EcommerceCartItem.scss";
import { Socket } from "socket.io-client";
import UIService from "../../../services/UIService";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { ProcessOrderEntryItemFn } from "@models/OrderEntryTypes";
import { startCase } from "lodash-es";

type Props = {
    orderItem: IOrder_Item;
    order: IOrder;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    selectElementToDelete: (row: IOrder_Item, index: number) => void;
    updateOrderEntryItem: ProcessOrderEntryItemFn;
    isReadOnly: boolean;
    isInSidebar: boolean;
    index: number;
};

const EcommerceCartItem: React.FC<Props> = ({
    orderItem,
    order,
    userModel,
    supplierModel,
    socket,
    notifyFn,
    selectElementToDelete,
    updateOrderEntryItem,
    isReadOnly,
    isInSidebar,
    index,
}) => {
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [qty, setQty] = useState(Number(orderItem.qty || 0));

    const prefix = "$";
    const pricingValue = orderItem.net_price;

    const [disabledAccept, setDisabledAccept] = useState("");
    const [isAcceptDisabled, setIsAcceptDisabled] = useState(false);

    useEffect(() => {
        if (orderItem.Catalog_Item?.min_order_qty) {
            if (qty < orderItem.Catalog_Item.min_order_qty) {
                setDisabledAccept("text-muted not-allowed");
                setIsAcceptDisabled(true);
            } else {
                setDisabledAccept("");
                setIsAcceptDisabled(false);
            }
        }
    }, [qty, orderItem.Catalog_Item?.min_order_qty]);

    const onUpdateOrderEntryItem = (data: IOnUpdateOrderEntryItemDTO) => {
        if (data.order_entry_item.guid === orderItem.guid) {
            const notification: INotification = {
                message: "successfully_updated_item",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            notifyFn(null, notification);
            setEditMode(false);
        }
    };

    useEffect(() => {
        const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryItem, fn: onUpdateOrderEntryItem }];

        // Subscribe to socket events
        for (const socketSetup of sockets) {
            socket.on(socketSetup.endpoint, socketSetup.fn);
        }

        // Cleanup function
        return () => {
            for (const socketSetup of sockets) {
                socket.removeListener(socketSetup.endpoint, socketSetup.fn);
            }
        };
    }, [socket, orderItem.guid, notifyFn]);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        if (name === "qty") {
            setQty(value);
        }
    };

    const toggleEditMode = () => {
        setEditMode(!editMode);
    };

    const updateItemQty = () => {
        const orderItemUI = orderItem as IOrder_ItemUI;
        orderItemUI.isDirty = true;
        orderItemUI.rowEditMode = true;
        orderItemUI.qty = qty;

        updateOrderEntryItem(orderItemUI, { shouldAutoSave: true });

        setEditMode(false);
    };

    return (
        <div id={`order-item-row-${index}`} className={"EcommerceCartItem position-relative"}>
            <div className={"details-wrapper w-100"}>
                <div className={"image " + (!isInSidebar ? "normal" : "")}>
                    <OwS3ImageViewer
                        catalogItem={orderItem.Catalog_Item!}
                        isMicroView={isInSidebar}
                        attachmentIndex={0}
                        imageIndex={IImageSizeEnum.THUMB}
                        className={""}
                        includeAllImages={false}
                        hideEmptyImage={false}
                    />
                </div>

                <div className="main-details flex-grow">
                    <div className="d-flex justify-content-between">
                        <div>
                            <div className={isInSidebar ? "" : "fw-bold"}>{orderItem.Catalog_Item!.name!}</div>
                        </div>

                        <div>
                            {isReadOnly ? null : (
                                <div className={"delete text-end"}>
                                    <div>
                                        <NumericFormat
                                            value={orderItem.total}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={prefix}
                                            decimalScale={supplierModel.pricing_precision || 2}
                                            fixedDecimalScale={true}
                                        />
                                    </div>
                                    <i className={"fad fa-trash-alt text-danger"} onClick={() => selectElementToDelete(orderItem, index)} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={"item-details"}>
                        <div>{orderItem.Catalog_Item!.sku!}</div>

                        {orderItem.Order_Item_Options && orderItem.Order_Item_Options.length > 0 ? (
                            <div className={"mt-3 mb-3"}>
                                {orderItem.Order_Item_Options?.map((option) => {
                                    return (
                                        <div key={option.guid}>
                                            <b>{option.Item_Option?.name}:</b> {startCase(option.value)}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}

                        {orderItem.Catalog_Item!.is_kit ? (
                            <div className={"mt-3 mb-3"}>
                                {orderItem.Kit_Items?.map((kitItem) => {
                                    return <div key={kitItem.guid}>{kitItem.Catalog_Item!.name}</div>;
                                })}
                            </div>
                        ) : null}
                        <div className={"price"}>
                            <NumericFormat
                                value={pricingValue}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={prefix}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                        </div>
                    </div>

                    <div className={"qty"}>
                        <div className={"me-2"}>
                            {editMode || isReadOnly ? null : <i className={"fas fa-pencil text-primary me-2"} onClick={toggleEditMode} />}
                        </div>

                        {editMode ? null : (
                            <div className={"qty-normal-view"}>
                                <OwLabel title={"qty"} postText={":"} className={"me-2"} />
                                <NumericFormat
                                    value={orderItem.qty}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={supplierModel.qty_precision || 0}
                                    fixedDecimalScale={true}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={"qty-edit-wrapper"}>
                {editMode ? (
                    <div>
                        <div className={"qty-edit mt-2"}>
                            <div className={"d-flex me-3"}>
                                <i
                                    className={"fas fa-check text-success me-2 pointer " + disabledAccept}
                                    onClick={() => {
                                        if (!isAcceptDisabled) {
                                            updateItemQty();
                                        }
                                    }}
                                />
                                <i className={"fas fa-times text-danger pointer "} onClick={toggleEditMode} />
                            </div>
                            <OwLabel title={"qty"} postText={":"} className={"me-2"} noBottomMargin={true} />
                            <div className={"qty-edit-view"}>
                                <OwInputWrapper
                                    name={"qty"}
                                    type={"number"}
                                    fn={handleChange}
                                    value={qty}
                                    supplierModel={supplierModel}
                                    onWheelFn={UIService.stopScrollWheel}
                                />
                            </div>
                        </div>

                        <div>
                            {orderItem.Catalog_Item?.min_order_qty ? (
                                <div className={"text-small text-danger text-start"}>
                                    <OwLabel title={"min_order_qty"} postText={":"} /> {orderItem.Catalog_Item?.min_order_qty}
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EcommerceCartItem;
